/**
 * Turns a failed sign-in into ONE clear message plus a trip to the login page.
 *
 * Registered FIRST in app.module so this catchError wraps MsalInterceptor's. An expired
 * session fails INSIDE MSAL as a client-side error carrying no HTTP status (AB#34659),
 * so matching only err.status === 401 misses it entirely and every component's own
 * generic "Unable to load ..." handler toasts instead.
 */
import { Injectable, Injector } from "@angular/core";
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { catchError } from "rxjs/operators";
import { EMPTY, Observable, throwError } from "rxjs";
import { Router } from "@angular/router";
import { BrowserAuthError, InteractionRequiredAuthError } from "@azure/msal-browser";
import { AuthService } from "../services/auth.service";
import { AlertService } from "../../shared/alert/alert.service";
import { environment } from "environments/environment";

// MSAL's code for "a redirect or popup is already running". Compared as a literal
// rather than through BrowserAuthErrorCodes so this does not depend on which MSAL
// minor version exports that map.
const INTERACTION_IN_PROGRESS = "interaction_in_progress";

// One dead session fails every in-flight request at once. Handle the first of the burst
// and stay quiet for the rest, so the user gets one message rather than a stack of
// toasts and logout() runs once rather than once per failed request.
const AUTH_FAILURE_BURST_MS = 5000;

// Transport failures, not sign-in failures. MSAL raises these as BrowserAuthError too,
// and treating a dropped connection as an expired session would sign a working user
// out and lose whatever they had part-finished.
const TRANSPORT_ERROR_CODES = [
  "post_request_failed",
  "no_network_connectivity",
  "get_request_failed",
  "failed_to_parse_response"
];

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private lastAuthFailureAt = 0;

  // AuthService is resolved on demand rather than injected. It depends on HttpClient,
  // and an interceptor that asks for HttpClient-dependent services while the
  // interceptor chain is being built is a cyclic dependency. Resolving it only once a
  // request has already failed means HttpClient is long since constructed.
  constructor(
    private _injector: Injector,
    private _router: Router,
    private _alertService: AlertService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only our own API can tell us anything about the CMS session. Receipt uploads go
    // through this same HttpClient to an Azure Function URL carrying its own ?code= key
    // (expense.service.ts), and a 401 from that means the function key is wrong, not
    // that the user is signed out - so it must not trigger a logout.
    const isOurApi = request.url.startsWith(environment.apiUrl);

    return next.handle(request).pipe(catchError(err => {
      // A sign-in redirect is already under way, so this tab is about to navigate away.
      // Anything said here is noise the user never gets time to read.
      if (this.isInteractionInProgress(err)) {
        return EMPTY;
      }

      if (isOurApi && this.isAuthFailure(err)) {
        this.handleAuthFailure();

        // EMPTY, not throwError: the calling component's error handler must not stack
        // "Unable to load list of brands" on top of the session-expired message.
        return EMPTY;
      }

      if (isOurApi && err?.status === 403) {
        // Access to the request is forbidden (user probably doesn't have permissions)
        void this._router.navigate(['/unauthorised']);
        return EMPTY;
      }

      return throwError(() => err);
    }));
  }

  /**
   * True when the request failed because it could not be authenticated: either the API
   * rejected the token, or MSAL could not obtain one. The MSAL cases never reach the
   * network, so they carry no HTTP status - which is why err.status alone is not enough.
   */
  private isAuthFailure(err: any): boolean {
    if (err instanceof BrowserAuthError && TRANSPORT_ERROR_CODES.includes(err.errorCode)) {
      return false;
    }

    return err?.status === 401
      || err instanceof InteractionRequiredAuthError
      || err instanceof BrowserAuthError;
  }

  private isInteractionInProgress(err: any): boolean {
    return err instanceof BrowserAuthError && err.errorCode === INTERACTION_IN_PROGRESS;
  }

  private handleAuthFailure(): void {
    const now = Date.now();

    if (now - this.lastAuthFailureAt < AUTH_FAILURE_BURST_MS) {
      return;
    }

    this.lastAuthFailureAt = now;

    this._alertService.showWarningAlert("Your session has expired. Please sign in again.");
    this._injector.get(AuthService).logout(true);
  }
}
